import React, { useState, useMemo } from "react";
import { Card, CardBody, CardTitle, Table, Badge, Button, Row, Col } from "reactstrap";
import formatCurrency from "constants/formatCurrency";
import { FILES_URL } from "helpers/url_helper";

const EmployeesPerformanceTable = ({ employees }) => {
    const [sortConfig, setSortConfig] = useState({ key: 'totalEarnings', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const sortedData = useMemo(() => {
        let sortableItems = [...(employees || [])];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [employees, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // Pagination
    const totalItems = sortedData.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const currentData = sortedData.slice(startIndex, startIndex + pageSize);

    if (!employees || employees.length === 0) return null;

    return (
        <Card style={{ backgroundColor: "#2f2f2f", borderRadius: 14, border: "1px solid rgba(255,255,255,0.1)" }}>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <CardTitle className="h4 mb-0" style={{ color: "#fff", fontWeight: 700 }}>
                        Employee Performance
                    </CardTitle>
                    <div style={{ color: "#ccc", fontSize: 13 }}>
                        Showing {startIndex + 1} - {Math.min(startIndex + pageSize, totalItems)} of {totalItems}
                    </div>
                </div>

                <div className="table-responsive">
                    <Table className="table-dark table-hover align-middle mb-0" style={{ fontSize: 14 }}>
                        <thead className="table-light">
                            <tr>
                                <th style={{ borderTopLeftRadius: 8, padding: "12px 16px" }}>Employee Name</th>
                                <th style={{ padding: "12px 16px" }}>Locations</th>
                                <th style={{ padding: "12px 16px" }}>Pay Rates</th>
                                <th
                                    className="text-end"
                                    onClick={() => requestSort('totalNetHours')}
                                    style={{ padding: "12px 16px", cursor: 'pointer', userSelect: 'none' }}
                                >
                                    Hours {sortConfig.key === 'totalNetHours' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                </th>
                                <th
                                    className="text-end"
                                    onClick={() => requestSort('totalEarnings')}
                                    style={{ borderTopRightRadius: 8, padding: "12px 16px", cursor: 'pointer', userSelect: 'none' }}
                                >
                                    Income {sortConfig.key === 'totalEarnings' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentData.map((emp) => {
                                // Calculate unique pay rates from shifts
                                const rates = new Set();
                                if (emp.shifts) emp.shifts.forEach(s => rates.add(parseFloat(s.pay_rate)));
                                const ratesArr = Array.from(rates).sort((a, b) => a - b);

                                return (
                                    <tr key={emp.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                                        <td style={{ padding: "12px 16px" }}>
                                            <div className="d-flex align-items-center gap-3">
                                                <div
                                                    style={{
                                                        width: 40, height: 40,
                                                        borderRadius: "50%",
                                                        overflow: "hidden",
                                                        border: "2px solid #FFCA5C",
                                                        backgroundColor: "#000",
                                                        flexShrink: 0,
                                                        display: "flex", alignItems: "center", justifyContent: "center"
                                                    }}
                                                >
                                                    {emp.picture ? (
                                                        <img
                                                            src={`${FILES_URL}${emp.picture}`}
                                                            alt=""
                                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                        />
                                                    ) : (
                                                        <i className="bx bxs-user" style={{ fontSize: "20px", color: "#fff" }}></i>
                                                    )}
                                                </div>
                                                <span style={{ fontWeight: 600 }}>
                                                    {emp.full_name || emp.employee_alias}
                                                </span>
                                            </div>
                                        </td>
                                        <td style={{ padding: "12px 16px" }}>
                                            <div className="d-flex flex-wrap gap-1">
                                                {emp.locationNames && Array.from(emp.locationNames).slice(0, 2).map((loc, i) => (
                                                    <Badge key={i} color="dark" style={{ border: "1px solid #555" }}>{loc}</Badge>
                                                ))}
                                                {emp.locationNames && emp.locationNames.size > 2 && (
                                                    <Badge color="dark" style={{ border: "1px solid #555" }}>+{emp.locationNames.size - 2}</Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: "12px 16px" }}>
                                            {ratesArr.map((r, i) => (
                                                <Badge key={i} color="info" className="me-1 bg-opacity-10 text-info border border-info border-opacity-25">
                                                    ${r}
                                                </Badge>
                                            ))}
                                        </td>
                                        <td className="text-end" style={{ padding: "12px 16px", fontWeight: 600, color: "#FFCA5C" }}>
                                            {emp.totalNetHours.toFixed(2)}
                                        </td>
                                        <td className="text-end" style={{ padding: "12px 16px", fontWeight: 600, color: "#22C55E" }}>
                                            ${formatCurrency(emp.totalEarnings)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>

                {/* Pagination Controls */}
                {totalItems > pageSize && (
                    <Row className="align-items-center mt-3">
                        <Col className="text-end">
                            <Button
                                color="secondary"
                                size="sm"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                style={{ marginRight: 8 }}
                            >
                                Previous
                            </Button>
                            <span style={{ color: "#fff", margin: "0 10px", fontSize: 14 }}>
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                color="secondary"
                                size="sm"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                style={{ marginLeft: 8 }}
                            >
                                Next
                            </Button>
                        </Col>
                    </Row>
                )}
            </CardBody>
        </Card>
    );
};

export default EmployeesPerformanceTable;